import {
	mountVideoPlayer,
	parseVideoUrl,
	sendEmbedPlayerCommand,
} from '../utils/parse-video-url.js';

const MODAL_SELECTOR = '.js-cs-video-modal';
const PLAY_SELECTOR = '.js-cs-grid-card-video-play, .js-testimonials-video-play';

let scrollLockY = 0;

const lockPageScroll = () => {
	scrollLockY = window.scrollY ?? document.documentElement.scrollTop;
	document.body.classList.add('cs-video-modal-open');
	document.body.style.position = 'fixed';
	document.body.style.top = `-${scrollLockY}px`;
	document.body.style.left = '0';
	document.body.style.right = '0';
	document.body.style.width = '100%';
};

const unlockPageScroll = () => {
	document.body.classList.remove('cs-video-modal-open');
	document.body.style.position = '';
	document.body.style.top = '';
	document.body.style.left = '';
	document.body.style.right = '';
	document.body.style.width = '';
	window.scrollTo(0, scrollLockY);
};

const setupMobilePlaybackControl = (mediaEl, parsed) => {
	if (!isCoarsePointerDevice() || parsed.type === 'file') {
		return;
	}

	const iframe = mediaEl.querySelector('iframe');
	if (!iframe) {
		return;
	}

	mediaEl.querySelector('.js-cs-video-modal-playback')?.remove();

	let isPlaying = true;
	const btn = document.createElement('button');
	btn.type = 'button';
	btn.className = 'cs-video-modal__playback js-cs-video-modal-playback';
	btn.dataset.state = 'playing';
	btn.setAttribute('aria-label', 'Pause video');
	btn.setAttribute('aria-pressed', 'true');
	btn.innerHTML =
		'<span class="cs-video-modal__playback-icon" aria-hidden="true"></span><span class="cs-video-modal__playback-label">Pause</span>';
	btn.disabled = true;

	iframe.addEventListener(
		'load',
		() => {
			btn.disabled = false;
		},
		{ once: true },
	);

	btn.addEventListener('click', (event) => {
		event.preventDefault();
		event.stopPropagation();

		const command = isPlaying ? 'pauseVideo' : 'playVideo';
		sendEmbedPlayerCommand(iframe, parsed, command);
		isPlaying = !isPlaying;

		btn.dataset.state = isPlaying ? 'playing' : 'paused';
		btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
		btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
		btn.querySelector('.cs-video-modal__playback-label').textContent = isPlaying
			? 'Pause'
			: 'Play';
	});

	mediaEl.append(btn);
};

const isCoarsePointerDevice = () =>
	window.matchMedia('(hover: none) and (pointer: coarse)').matches;

const resolveVideoSource = (trigger) => {
	const fileSrc = trigger.dataset.videoFile?.trim();
	if (fileSrc) {
		return { type: 'file', src: fileSrc };
	}

	return parseVideoUrl(trigger.dataset.videoUrl?.trim());
};

const getModal = () => {
	let modal = document.querySelector(MODAL_SELECTOR);
	if (modal) {
		return modal;
	}

	modal = document.createElement('div');
	modal.className = 'cs-video-modal js-cs-video-modal';
	modal.hidden = true;
	modal.innerHTML = `
		<div class="cs-video-modal__backdrop js-cs-video-modal-close" tabindex="-1"></div>
		<div class="cs-video-modal__panel">
			<button type="button" class="cs-video-modal__close js-cs-video-modal-close" aria-label="Close video">
				<span aria-hidden="true">&times;</span>
			</button>
			<div class="cs-video-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cs-video-modal-title">
				<h2 id="cs-video-modal-title" class="visually-hidden">Video</h2>
				<div class="cs-video-modal__media js-cs-video-modal-media"></div>
			</div>
		</div>
	`;
	document.body.append(modal);
	return modal;
};

let lastActiveElement = null;

const closeModal = (modal) => {
	const mediaEl = modal.querySelector('.js-cs-video-modal-media');
	if (mediaEl) {
		mediaEl.replaceChildren();
	}

	modal.hidden = true;
	unlockPageScroll();

	if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
		lastActiveElement.focus();
	}

	lastActiveElement = null;
};

const openModal = (modal, parsed, label) => {
	const mediaEl = modal.querySelector('.js-cs-video-modal-media');
	const titleEl = modal.querySelector('#cs-video-modal-title');
	if (!mediaEl) {
		return;
	}

	if (titleEl && label) {
		titleEl.textContent = label;
	}

	mountVideoPlayer(mediaEl, parsed);
	setupMobilePlaybackControl(mediaEl, parsed);
	modal.hidden = false;
	lockPageScroll();
	modal.querySelector('.cs-video-modal__close')?.focus({ preventScroll: true });
};

const bindModal = (modal) => {
	if (modal.dataset.bound === 'true') {
		return;
	}

	modal.dataset.bound = 'true';

	modal.querySelectorAll('.js-cs-video-modal-close').forEach((el) => {
		el.addEventListener('click', () => closeModal(modal));
	});

	modal.addEventListener('click', (event) => {
		if (event.target === modal) {
			closeModal(modal);
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !modal.hidden) {
			closeModal(modal);
		}
	});
};

const bindPlayButton = (modal, trigger) => {
	trigger.addEventListener('click', (event) => {
		event.preventDefault();
		event.stopPropagation();

		const parsed = resolveVideoSource(trigger);
		if (!parsed) {
			return;
		}

		lastActiveElement = trigger;
		const label = trigger.getAttribute('aria-label') || 'Video';
		openModal(modal, parsed, label);
	});
};

export const initCsGridCardVideoModal = () => {
	const triggers = document.querySelectorAll(PLAY_SELECTOR);
	if (!triggers.length) {
		return;
	}

	const modal = getModal();
	bindModal(modal);
	triggers.forEach((trigger) => bindPlayButton(modal, trigger));
};
